import { useEffect, useRef, useState } from 'react'
import { MARK_OPTIONS, stateLabel, stateTone, type AttendanceState } from '../api/attendance'

/**
 * One note box, with the whole of its save behaviour (D-656).
 *
 * **Extracted so two audiences share one implementation**, not for tidiness.
 * What lives here is subtle and was got wrong twice: a save on blur, a second
 * save at unmount because detaching a focused input fires no blur, a ref rather
 * than state so the cleanup reads what is actually in the box, and a sync that
 * follows the record without clobbering a half-written sentence mid-flight.
 * Two copies of that would drift, and the drift would lose somebody's words.
 */
function NoteBox({
  label,
  placeholder,
  value,
  onSave,
}: {
  label: string
  placeholder: string
  value?: string | null
  /** The text the box is showing, empty string included — that is how one is cleared. */
  onSave: (text: string) => void
}) {
  const [draft, setDraft] = useState(value ?? '')
  const [typing, setTyping] = useState(false)

  const pending = useRef<string | null>(null)
  const save = useRef(onSave)
  save.current = onSave

  /*
   * <b>Follows the record, but never past what she has written.</b>
   *
   * An earlier version cleared `typing` on blur and let this run, which
   * overwrote the box with the *saved* value while the save was still in
   * flight - so an edit visibly reverted for a round trip and read as "my
   * change did not take". Keyed on the saved value instead: when the record
   * comes back carrying what she wrote there is nothing left to hold, and any
   * other arrival is somebody else's edit and should show.
   */
  useEffect(() => {
    if (!typing || (value ?? '') === draft) setDraft(value ?? '')
  }, [value, typing, draft])

  /*
   * <b>Save what she typed if the screen goes before the box blurs.</b>
   *
   * Blur was the only save path, and detaching a focused input does not fire
   * one - so Escape on the roster panel (D-515) and the tablet's own
   * session-ended close both threw the sentence away silently. Clicking Close
   * blurs first in a real browser, which is why this is the kind of hole that
   * survives every manual test: the two paths that lose data are the two
   * nobody thinks to try.
   */
  useEffect(
    () => () => {
      if (pending.current !== null) save.current(pending.current)
    },
    [],
  )

  return (
    <label className="mt-2 block">
      <span className="sr-only">{label}</span>
      <input
        type="text"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => {
          setTyping(true)
          setDraft(e.target.value)
          pending.current = (value ?? '') === e.target.value ? null : e.target.value
        }}
        /*
          Saved on blur rather than behind its own button. One control and one
          trip: she is already moving to the next dancer, and a Save she has to
          find is a note that does not get written.
        */
        onBlur={() => {
          if ((value ?? '') !== draft) onSave(draft)
          else setTyping(false)
          pending.current = null
        }}
        className="w-full rounded-xl border border-line-2 bg-surface px-3 py-2 text-[13.5px]"
      />
    </label>
  )
}

/**
 * The four ways a teacher marks a dancer, and the note beside them (D-639).
 *
 * <b>One control, because there were three and they disagreed.</b> The wall
 * tablet and the fill-it-in-afterwards screen each hand-rolled the same grid
 * over `MARK_OPTIONS`; the teacher's own phone did something else entirely - a
 * single chip cycling Here and Absent, with everything else collapsing to Here
 * on the next tap. So `Watched` arrived on two of the three surfaces and the
 * commit that added it never touched the third, because the third mapped
 * nothing to add to. That is A-17 stated as a bug report from the pilot: *"the
 * teachers would like a way to mark if a kid watched"*, built, and then invisible
 * to the teacher who asked, because she takes her register on a phone.
 *
 * <b>Notes were never built anywhere.</b> The storage has existed since D-188
 * and no client had ever written to it, which is also why nothing had noticed
 * that marking erased it.
 *
 * <b>What it deliberately does not offer is Excused.</b> Forgiving a session
 * decides who competes and is Tonya's alone (D-188); it stays a state, set from
 * her screen, and never a button in the room.
 */
export function MarkButtons({
  dancerName,
  state,
  note,
  familyNote,
  onMark,
  columns = 2,
  locked = false,
  busy = false,
}: {
  /**
   * Whose row this is, for the accessible name.
   *
   * <b>Twelve rows of a button labelled `Here` are one button to a screen
   * reader.</b> The visible word stays short because the grid is four across on
   * a phone; the name a reader hears says which child it marks.
   */
  dancerName: string
  state: AttendanceState
  /** The studio-only note already on the record (D-656). */
  note?: string | null
  /** What the family may read about this session (D-656). */
  familyNote?: string | null
  /**
   * Either note undefined means *leave that one alone*, which is what a state
   * button sends and what the domain reads (D-639, D-656). A box sends the text
   * it is showing, empty string included, because that is how one is cleared —
   * and never touches the other, or saving one audience's note would blank the
   * other's.
   */
  onMark: (state: AttendanceState, note?: string, familyNote?: string) => void
  /** Two columns on a narrow tablet row, four where there is room. */
  columns?: 2 | 4
  /**
   * Her family reported her out and nobody has confirmed otherwise, so a tap
   * asks first rather than marking (D-484). The requested state travels out
   * with it - this control offers four, and a dialog that assumed Here would
   * quietly turn a Watched into an attendance.
   */
  locked?: boolean
  busy?: boolean
}) {
  const marked = state !== 'Unmarked'

  /*
   * ⚠ <b>`NotExpected` rendered as four blank buttons</b> (D-855), which is
   * pixel-identical to <i>nobody has marked her</i> — the one state a register exists
   * to distinguish (D-118) — and it arrives twenty-seven rows at a time.
   *
   * <b>`Excused` is deliberately not the other half of this.</b> A first draft
   * captioned any state outside the four and said so in the decision entry, reasoning
   * that an excused dancer had always looked unmarked here too. **She never has:**
   * `AttendanceService.RegisterState` maps `Excused` to `Unexcused` for every reader
   * including Tonya, so a register row reads <i>Absent</i> with the Absent button lit
   * and no sheet can ever send `Excused` to this component. The caption was dead code,
   * its test was green only because a fixture handed it a state no endpoint sends, and
   * its wording — <i>set by the studio</i> — would have disclosed the excusal that
   * `ExcuseAbsences` exists to keep from a teacher. An audit found all three.
   *
   * <b>It is also what gives `stateLabel` its first caller.</b> A red team found the
   * fall-through in that function, where an unnamed state read <i>Not marked</i>; the
   * fix was right and reached no screen, because nothing outside the tests called it.
   * A correct answer nobody asks for is the shape this codebase keeps paying for.
   */
  const notCalled = state === 'NotExpected'

  return (
    <div className="mt-2.5">
      {notCalled && (
        <p className="mb-2 text-[12.5px] font-semibold text-ink-2">
          {stateLabel(state)}
          <span className="ml-1.5 font-normal text-ink-3">
            {/* She may still have turned up, and then this is her row (S1.5). */}
            &mdash; mark her below if she was here after all
          </span>
        </p>
      )}

      <div className={`grid gap-2 ${columns === 4 ? 'grid-cols-4' : 'grid-cols-2'}`}>
        {MARK_OPTIONS.map((option) => {
          const on = state === option.state

          return (
            <button
              key={option.state}
              type="button"
              aria-pressed={on}
              aria-label={`Mark ${dancerName} ${option.label}`}
              /*
                <b>Never disabled while a save is in flight.</b> A teacher
                sweeping a roster taps again long before the first round trip
                lands, and a disabled button swallows it - leaving her with the
                state she was correcting. The queue behind this accepts every
                tap; `busy` is for the tint, not the gate.
              */
              onClick={() => onMark(option.state)}
              className={`rounded-xl border py-3 text-[14px] font-semibold ${
                on ? TONE[locked ? 'warn' : stateTone(option.state)] : 'border-line-2 bg-surface text-ink-3'
              } ${busy ? 'opacity-70' : ''}`}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {/*
        The note appears once she has marked somebody, because an empty roster
        of twelve note boxes is a screen nobody can sweep - and the question it
        answers ("why were they watching") only exists after a state is chosen.
      */}
      {marked && (
        <>
          {/*
            <b>Two boxes, two audiences</b> (D-656). `asked`: *"a staff notes
            section not visible to parents, and a parent notes section visible
            to both."*

            <b>Not one box with a switch.</b> A teacher writing "watch the ankle,
            do not push it" for the studio and "she did beautifully today" for
            the family is the whole point of the ask; a switch would make her
            choose one audience per session.

            <b>Labelled by who reads it, never by what it is.</b> "Note" and
            "Note 2" is a screen where the wrong sentence reaches a parent -
            and that mistake cannot be taken back.
          */}
          <NoteBox
            label={`Studio note about ${dancerName} — the family never sees this`}
            placeholder="For the studio only"
            value={note}
            onSave={(text) => onMark(state, text)}
          />
          <NoteBox
            label={`Note for ${dancerName}'s family`}
            placeholder="The family can read this"
            value={familyNote}
            onSave={(text) => onMark(state, undefined, text)}
          />
        </>
      )}
    </div>
  )
}

/**
 * Semantic colour for a state, through `stateTone` so one map answers.
 *
 * Two copies of this existed and a third disagreed with both: the phone's chip
 * asked for `border-ok bg-ok-bg text-ok`, and no `--color-ok` token has ever
 * been defined - so the most-tapped state on the most-used surface rendered
 * with no colour at all, and nothing could catch it because a missing Tailwind
 * utility is silence rather than an error.
 */
const TONE: Record<'good' | 'warn' | 'crit' | 'mute', string> = {
  good: 'bg-good-bg text-good border-good',
  warn: 'bg-warn-bg text-warn border-warn',
  crit: 'bg-crit-bg text-crit border-crit',
  mute: 'bg-surface-2 text-ink-2 border-line-2',
}
